import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router'
import moment from 'moment-timezone'
import {
  ArrowsDownUpIcon,
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  PlusIcon,
  TreeStructureIcon,
} from '@phosphor-icons/react'
import { Tribe } from '../../types/Tribe'
import { User } from '../../types/User'
import {
  useCreateTribeMutation,
  useLazyGetAllTribesQuery,
} from '../../redux/tribe.endpoints'
import { toast } from '../../utils/toast'
import { TribeModal } from '../../components/TribeModal/TribeModal'

interface Props {
  user: User
}

type SortColumn = 'name' | 'created_at'

const activeTeams = (tribe: Tribe) =>
  tribe.teams.filter(
    team => !team.endDate || new Date(team.endDate).valueOf() > Date.now(),
  )

const TribesView = ({ user }: Props) => {
  const navigate = useNavigate()
  const [tribes, setTribes] = useState<Tribe[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState<SortColumn>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [isModalOpen, setModalOpen] = useState(false)

  const [getTribes, { data }] = useLazyGetAllTribesQuery()
  const [createTribe] = useCreateTribeMutation()

  useEffect(() => {
    getTribes({
      companyId: user.company.id,
      pageSize: 0,
      pageNumber: 0,
      sortBy: 'id',
      searchQuery: '',
      sortType: 'DESC',
    })
  }, [getTribes, user.company.id])

  useEffect(() => {
    setTribes(data || [])
  }, [data])

  const handleSort = (column: SortColumn) => {
    if (column === sortColumn) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const visibleTribes = useMemo(() => {
    const filtered = tribes.filter(tribe =>
      tribe.name.toLowerCase().includes(searchValue.toLowerCase()),
    )
    return [...filtered].sort((a, b) => {
      const comparison =
        sortColumn === 'created_at'
          ? moment(a.created_at).valueOf() - moment(b.created_at).valueOf()
          : a.name.localeCompare(b.name)
      return sortOrder === 'asc' ? comparison : -comparison
    })
  }, [tribes, searchValue, sortColumn, sortOrder])

  const handleSaveTribe = async (name: string) => {
    try {
      await createTribe({
        name,
        isActive: true,
        description: '',
        companyId: user.company.id,
        isDeliveryTribe: false,
      }).unwrap()
      getTribes({
        companyId: user.company.id,
        pageSize: 0,
        pageNumber: 0,
        sortBy: 'id',
        searchQuery: '',
        sortType: 'DESC',
      })
      setModalOpen(false)
    } catch (error: any) {
      toast.error(
        `An error occurred when creating the tribe: ${
          error?.data?.error ?? 'Unknown error'
        }`,
        { toastId: 'create-tribe-error' },
      )
      return false
    }
  }

  const sortableHeader = (label: string, column: SortColumn, width: string) => (
    <th className="manage-tribes__table-th" style={{ width }}>
      <button
        type="button"
        className="manage-tribes__sort-btn"
        onClick={() => handleSort(column)}
      >
        {label}
        <ArrowsDownUpIcon size={14} weight="regular" color="#717182" />
      </button>
    </th>
  )

  return (
    <div className="container my-4 manage-tribes">
      <Helmet title="Time Off - Tribes" />

      <div className="manage-tribes__header">
        <h1 className="manage-tribes__title">Manage Tribes</h1>
        <button
          type="button"
          className="manage-tribes__add-btn"
          onClick={() => setModalOpen(true)}
        >
          <PlusIcon size={16} weight="regular" color="#ffffff" />
          Add New Tribe
        </button>
      </div>

      <div className="manage-tribes__filters">
        <div className="manage-tribes__search">
          <input
            type="text"
            className="manage-tribes__search-input"
            placeholder="Search by Tribe"
            aria-label="Search by Tribe"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
          />
          <MagnifyingGlassIcon
            size={16}
            weight="regular"
            className="manage-tribes__search-icon"
          />
        </div>
      </div>

      {visibleTribes.length === 0 ? (
        <div className="manage-tribes__empty">
          <div className="manage-tribes__empty-icon">
            <TreeStructureIcon size={48} weight="regular" color="#717182" />
          </div>
          <p className="manage-tribes__empty-label">
            No tribes found. Try adjusting your search.
          </p>
        </div>
      ) : (
        <div className="manage-tribes__table-card">
          <table className="manage-tribes__table">
            <thead className="manage-tribes__table-head">
              <tr>
                {sortableHeader('Tribe', 'name', '20%')}
                {sortableHeader('Created On', 'created_at', '18%')}
                <th
                  className="manage-tribes__table-th"
                  style={{ width: '50%' }}
                >
                  Teams
                </th>
                <th
                  className="manage-tribes__table-th"
                  style={{ width: '12%' }}
                >
                  Members
                </th>
                <th className="manage-tribes__table-th manage-tribes__table-th--actions" />
              </tr>
            </thead>
            <tbody>
              {visibleTribes.map(tribe => (
                <tr
                  key={tribe.id}
                  className="manage-tribes__table-row"
                  onClick={() => navigate(`/tribes/${tribe.id}`)}
                >
                  <td className="manage-tribes__table-td">{tribe.name}</td>
                  <td className="manage-tribes__table-td">
                    {moment(tribe.created_at).format('MM/DD/YYYY')}
                  </td>
                  <td className="manage-tribes__table-td manage-tribes__table-td--teams">
                    <div className="manage-tribes__teams-list">
                      {activeTeams(tribe).map(team => (
                        <span
                          key={team.id}
                          className="manage-tribes__team-pill"
                        >
                          {team.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="manage-tribes__table-td">
                    {tribe.activeMembersCount ?? 0}
                  </td>
                  <td className="manage-tribes__table-td manage-tribes__table-td--actions">
                    <button
                      type="button"
                      className="manage-tribes__edit-btn"
                      aria-label={`Edit ${tribe.name}`}
                      title="Edit"
                      onClick={e => {
                        e.stopPropagation()
                        navigate(`/tribes/${tribe.id}`)
                      }}
                    >
                      <PencilSimpleIcon size={18} weight="regular" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TribeModal
        show={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSaveTribe}
      />
    </div>
  )
}

export default TribesView
